import { psCollection } from "./powersync-collection.js";
import {
  orderSchema,
  orderItemSchema,
  orderItemOptionSelectionSchema,
  kitchenTicketSchema,
  type Order,
  type OrderItem,
  type OrderItemOptionSelection,
  type KitchenTicket,
} from "../schemas/sales.js";

export const ordersCollection = psCollection("orders", orderSchema);

export const orderItemsCollection = psCollection("order_items", orderItemSchema);

export const orderItemOptionSelectionsCollection = psCollection("order_item_option_selections", orderItemOptionSelectionSchema);

export const kitchenTicketsCollection = psCollection("kitchen_tickets", kitchenTicketSchema);

export interface CommitOrderItemInput {
  // A new item is never ready or served yet; the server fills both in as empty.
  row: Omit<OrderItem, "ready_at" | "served_at"> & Partial<Pick<OrderItem, "ready_at" | "served_at">>;
  selections?: OrderItemOptionSelection[];
}

type OnlineOrderField = "delivery" | "delivery_fee" | "payment_proof_key" | "payment_review" | "payment_reject_reason" | "fulfilment" | "guest_account_id";

export interface CommitOrderBatchInput {
  /** A brand-new order row to insert alongside `items` (first send for a table/pickup). */
  // Delivery details only exist on an online order; every other caller leaves them out.
  order?: Omit<Order, OnlineOrderField> & Partial<Pick<Order, OnlineOrderField>>;
  /** A partial update to an already-open order (appending items to it). */
  orderUpdate?: { id: string; changes: Partial<Order> };
  /** Only needed alongside `order` (a brand-new order) — its `order_id` FK
   * can't be satisfied until the order row exists, which for a new order
   * only happens inside this same request, so it can't be a separate
   * `kitchenTicketsCollection.insert()` beforehand (that raced ahead of the
   * order and tripped `kitchen_tickets_order_id_fkey`). Appending a ticket
   * to an already-open order doesn't have this problem — insert it
   * directly via `kitchenTicketsCollection.insert()` and pass the id into
   * `items` as usual, no need to route it through here. */
  kitchenTicket?: KitchenTicket;
  items: CommitOrderItemInput[];
  /** Folded into this same atomic transaction server-side — see
   * routes/sales.ts's `/commit` handler doc for why these three used to be
   * separate, non-idempotent client writes issued after this resolved (a
   * real bug: no protection if the app crashed in between, and the loyalty
   * balance was a client-side read-then-write race). The caller computes
   * *which* ingredients/how much (needs the cart's selected options); only
   * the write itself moved server-side. */
  stockDeductions?: Array<{ ingredientId: string; qty: number }>;
  loyaltyEarn?: { memberId: string; points: number };
  voucherRedeem?: { voucherId: string };
  /** Guest Portal: the signed-in account's token. The API puts the account on the order, so it follows them to any device. */
  guestAccountToken?: string;
  /** Guest Portal: the member ticket, so the server can put that member's voucher on the order. */
  memberTicket?: string;
  /** Paying part of the bill with points. The API checks the store's redeem rate and the member's balance, and sets the discount itself. */
  pointsRedeem?: { memberId: string; points: number };
  /** Present only for a manager-approved employee sale. The API verifies it against the store's enabled rate. */
  employeeDiscountPercent?: number;
}

export interface CommitOrderBatchResult {
  /** The order row as the server actually persisted it — use this (not `input.order`) for anything display-facing like order_number, since the server may have assigned it (see "AUTO" below). */
  order: Order | null;
  /** True when this exact batch had already committed from an earlier attempt (a retry after the original response was lost — see routes/sales.ts). The DB write itself (including stock/loyalty/voucher, now folded into the same transaction) is safely deduped either way — nothing left for a caller to gate on `duplicate` for anymore. */
  duplicate: boolean;
}

/**
 * Writes an order (or an update to an already-open one) plus its line
 * items and their customizer option selections in one server-side Postgres
 * transaction (POST /sales/orders/commit), instead of one collection
 * `.insert()`/`.update()` per row — each of which is its own independent
 * HTTP request/transaction and can let a child row (an item, or a
 * selection) reach the database before its parent's row has committed,
 * tripping the order_items/option_selections foreign keys.
 *
 * Pass `order.order_number: "AUTO"` to have the server assign the next
 * "#1001, #1002…" daily ticket number for that store atomically, instead
 * of computing it client-side — a plain count-then-write, which is exactly
 * what used to trip `uq_orders_number` when two new orders landed close
 * together. Callers with their own distinct numbering scheme (guest
 * portal's "G-…", the register app's "R-…") should keep sending their own
 * value as before.
 */
/**
 * Thrown by `commitOrderBatch` specifically when the request never reached
 * sync-api at all (offline, DNS failure, connection refused) — distinct
 * from a real HTTP-level rejection (a plain `Error`, thrown below), so a
 * caller like POS's PaymentModal can tell "genuinely offline, safe to queue
 * and retry later" apart from "the server actually said no, don't retry
 * blindly." Captured via a closure flag inside `mutationFn` rather than
 * relying on `instanceof TypeError` after the error has passed back through
 * TanStack DB's transaction machinery, which may wrap it.
 */
export class SyncApiNetworkError extends Error {
  /** The exact JSON body that failed to send — a caller that wants to queue
   * this for later (see apps/pos/src/lib/outbox.ts's `queueSale`) can POST
   * it byte-for-byte once connectivity returns, without needing to
   * reconstruct `commitOrderBatch`'s internal request shape itself. */
  requestBody: Record<string, unknown>;
  constructor(cause: unknown, requestBody: Record<string, unknown>) {
    super("Couldn't reach sync-api");
    this.name = "SyncApiNetworkError";
    this.cause = cause;
    this.requestBody = requestBody;
  }
}

